import { useState, type FormEvent } from "react";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, type Job, type RoleTermKey, type RoleTerms } from "../lib/api";
import { formatSalary } from "../lib/format";

/** The details the recruiter asks the client for, in the order a candidate would want them. */
const TERMS: { key: RoleTermKey; label: string; placeholder: string; long?: boolean }[] = [
  { key: "clientBusiness", label: "What the client does", placeholder: "e.g. Payments software for UK retailers, 200 staff" },
  { key: "startDate", label: "Start date", placeholder: "e.g. ASAP, or 1 December" },
  { key: "duration", label: "Duration", placeholder: "e.g. Permanent, or 6-month fixed term" },
  { key: "hours", label: "Hours", placeholder: "e.g. 37.5 a week, 2 days in the office" },
  { key: "benefits", label: "Benefits", placeholder: "e.g. 10% bonus, 6% pension, 27 days holiday" },
  { key: "payIntervals", label: "Paid", placeholder: "e.g. Monthly" },
  { key: "noticePeriod", label: "Notice period", placeholder: "e.g. 1 month in probation, then 3 months" },
  { key: "qualifications", label: "Experience and qualifications required", placeholder: "Including any licence, right to work or vetting the law requires", long: true },
  { key: "healthSafety", label: "Health and safety risks", placeholder: "Known risks and what the client does about them, or “None known”", long: true },
  { key: "expenses", label: "Expenses", placeholder: "Anything paid to or by the candidate, e.g. relocation, or “None”" },
];

/**
 * What the agency must find out from the client and pass on to the candidate before introducing
 * them (Conduct of Employment Agencies and Employment Businesses Regulations 2003, regs 18 and 21).
 * Shows what's still missing and turns it into text to send the candidate.
 */
export function JobRoleTerms({ job, onSaved }: { job: Job; onSaved: (job: Job) => void }) {
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const terms: RoleTerms = { ...job.roleTerms, clientBusiness: job.roleTerms.clientBusiness ?? job.companyIndustry ?? undefined };
  const salary = job.salaryMin ? formatSalary(job.salaryMin, job.salaryMax, job.currency) : "";
  const fromJob = [
    { label: "Location", ok: !!job.location },
    { label: "Salary", ok: !!job.salaryMin },
    { label: "Job description", ok: !!job.description.trim() },
  ];
  const missing = [...fromJob.filter((f) => !f.ok).map((f) => f.label), ...TERMS.filter((t) => !terms[t.key]).map((t) => t.label)];
  const total = fromJob.length + TERMS.length;

  const copy = () => void navigator.clipboard.writeText(candidateText(job, terms, salary)).then(() => setCopied(true));

  return (
    <section className="section" aria-labelledby="role-terms-h">
      <div className="section-head">
        <h2 id="role-terms-h" className="label">
          What the candidate must be told
        </h2>
        {!editing && (
          <button type="button" className="link-btn" onClick={() => setEditing(true)}>
            {TERMS.some((t) => terms[t.key]) ? "Edit" : "Fill in"}
          </button>
        )}
      </div>
      {editing ? (
        <RoleTermsForm
          job={job}
          initial={terms}
          onCancel={() => setEditing(false)}
          onSaved={(j) => {
            onSaved(j);
            setEditing(false);
            setCopied(false);
          }}
        />
      ) : (
        <>
          <p className={missing.length ? "notice" : "hint"} role="status">
            {missing.length === 0
              ? "Everything the regulations ask for is recorded."
              : `${total - missing.length} of ${total} recorded. Ask the client for: ${missing.join(", ").toLowerCase()}.`}
          </p>
          <p className="hint">
            The law says you must get these from the client and tell the candidate before introducing them (Conduct of Employment Agencies Regulations, regs 18
            and 21).
          </p>
          {TERMS.some((t) => terms[t.key]) && (
            <dl className="ledger">
              {TERMS.filter((t) => terms[t.key]).map((t) => (
                <div key={t.key} className={t.long ? "wide" : undefined}>
                  <dt className="label">{t.label}</dt>
                  <dd className="prewrap">{terms[t.key]}</dd>
                </div>
              ))}
            </dl>
          )}
          <div className="button-row spaced-top">
            <button type="button" className="btn secondary" onClick={copy}>
              Copy for the candidate
            </button>
            {copied && (
              <span className="hint" role="status">
                Copied. Paste it into your email or message to them.
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function RoleTermsForm({ job, initial, onCancel, onSaved }: { job: Job; initial: RoleTerms; onCancel: () => void; onSaved: (job: Job) => void }) {
  const [values, setValues] = useState<RoleTerms>(initial);
  const { pending, error, run } = useAction();
  const set = (key: RoleTermKey, v: string) => setValues((cur) => ({ ...cur, [key]: v }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const roleTerms = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, (v ?? "").trim()]).filter(([, v]) => v)) as RoleTerms;
    void run(async () => onSaved((await api.updateJob(job.id, { roleTerms })).job));
  };
  const short = TERMS.filter((t) => !t.long);
  return (
    <form className="form panel" onSubmit={submit}>
      <div className="grid-2">
        {short.map((t) => (
          <Field key={t.key} label={t.label} maxLength={2000} value={values[t.key] ?? ""} placeholder={t.placeholder} onChange={(e) => set(t.key, e.target.value)} />
        ))}
      </div>
      {TERMS.filter((t) => t.long).map((t) => (
        <label key={t.key} className="field">
          <span className="label">{t.label}</span>
          <textarea rows={2} maxLength={2000} value={values[t.key] ?? ""} placeholder={t.placeholder} onChange={(e) => set(t.key, e.target.value)} />
        </label>
      ))}
      <p className="hint">Location, salary and the description come from the job's details above.</p>
      <ErrorNotice error={error} />
      <div className="button-row">
        <button className="btn" disabled={pending}>
          {pending ? "Saving…" : "Save"}
        </button>
        <button type="button" className="link-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Plain text for an email or message to the candidate. Anything not recorded is left out. */
export function candidateText(job: Job, terms: RoleTerms, salary: string): string {
  const line = (label: string, value: string | null | undefined) => (value ? `${label}: ${value}` : "");
  return [
    `${job.title} at ${job.company.name}`,
    terms.clientBusiness ?? "",
    "",
    line("Location", job.location),
    line("Salary", salary),
    line("Benefits", terms.benefits),
    line("Paid", terms.payIntervals),
    line("Start date", terms.startDate),
    line("Duration", terms.duration),
    line("Hours", terms.hours),
    line("Notice period", terms.noticePeriod),
    line("Experience and qualifications required", terms.qualifications),
    line("Health and safety", terms.healthSafety),
    line("Expenses", terms.expenses),
    job.description.trim() ? `\nThe job description:\n${job.description.trim()}` : "",
  ]
    .filter((l, i) => l !== "" || i === 2)
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
